import { Card, CircularProgress, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import AlertBlock from "@/components/molecules/AlertBlock";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import SWR_KEYS from "@/constants/swr-keys";
import useGetAllCustomerSalesProducts from "@/hooks/requests/customer-sales-product/use-get-all-customer-sales-product";
import useUpdateCustomerSalesProduct from "@/hooks/requests/customer-sales-product/use-update-customer-sales-product";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import { CustomerMessages } from "@/messages/customer/customer.messages";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";

import ActivePlanView from "./components/ActivePlanView";
import { defaultPageNumber as pageNumber, defaultPageSize as pageSize } from "./constants";

type Props = {
  messages: CustomerMessages;
};

const PlansManagementTab = ({ messages }: Props) => {
  const { mutate } = useMutate();
  const { data, isLoading, error } = useGetAllCustomerSalesProducts({ pageNumber, pageSize });
  const isInitialLoading = isLoading && data === null;
  const showLoadError = Boolean(error) && data === null;
  const { update } = useUpdateCustomerSalesProduct();
  const { isVisible, handleOpen, handleClose, selected } = useDialog<ICustomerSalesProduct>();

  const { planUpdateSuccess, planUpdateError } = messages;

  const onConfirmCancel = async (selectedPlan: ICustomerSalesProduct) => {
    const successMessage = planUpdateSuccess;
    const errorMessage = planUpdateError;
    const _id = selectedPlan.id;
    const salesProductId = selectedPlan.salesProduct?.id ?? selectedPlan.salesProductId;
    const salesProductPriceId =
      selectedPlan.salesProductPrice?.id ?? selectedPlan.salesProductPriceId;
    const status = CustomerSalesProductStatus.Inactive;
    const formData = { ...selectedPlan, salesProductId, salesProductPriceId, status };
    const onSuccess = () => mutate(SWR_KEYS.getAllCustomerSalesProducts);

    await update({ _id, formData, successMessage, errorMessage, onSuccess });

    handleClose();
  };

  return (
    <MDBox mt={2}>
      <Grid container spacing={3}>
        <Grid size={12}>
          {isInitialLoading && (
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress color="inherit" />
              </MDBox>
              <Skeleton variant="rectangular" width="100%" height={280} />
            </MDBox>
          )}

          {showLoadError && error && (
            <Card>
              <AlertBlock
                title={messages.errorToLoad}
                description={error.message}
                alt={messages.notFoundAlt}
              />
            </Card>
          )}

          {!isInitialLoading && !showLoadError && (
            <ActivePlanView
              data={data?.items ?? []}
              messages={messages}
              onCancelPlan={handleOpen}
            />
          )}
        </Grid>
      </Grid>

      {selected && (
        <ConfirmationDialog
          isOpen={isVisible}
          onClose={handleClose}
          onConfirm={() => onConfirmCancel(selected)}
          titleText={messages.confirmCancelDialogTitle}
          descriptionText={messages.confirmCancelDialogDescription}
          cancelText={messages.cancelButton}
          confirmText={messages.confirmButton}
        />
      )}
    </MDBox>
  );
};

export default PlansManagementTab;
